import { memo, useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Collapse,
  Divider,
  Typography,
  useMediaQuery,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { useNavigate, useLocation } from "react-router-dom";
import { SharedMenuItem } from "../menuTypes";
import {
  usePrototypeSignatureSession,
  type PrototypeSignatureProvider,
} from "src-next/shared/prototype-signature";

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

export interface SharedSidebarProps {
  items: SharedMenuItem[];
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  container?: Element;
}

interface SidebarItemProps {
  item: SharedMenuItem;
  level: number;
  isDesktop: boolean;
  isSidebarExpanded: boolean;
  activePath: string;
  onNavigate: (item: SharedMenuItem) => void;
  isGroupOpen: boolean;
  toggleGroup: () => void;
}

const SidebarItem = memo<SidebarItemProps>(
  ({
    item,
    level,
    isDesktop,
    isSidebarExpanded,
    activePath,
    onNavigate,
    isGroupOpen,
    toggleGroup,
  }) => {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    const isActive =
      !!item.path && activePath.startsWith(item.path) && !hasChildren;

    const handleClick = () => {
      if (item.disabled) return;
      if (hasChildren) {
        toggleGroup();
        return;
      }
      if (item.path) {
        onNavigate(item);
      }
    };

    const paddingLeft = useMemo(() => {
      if (!isDesktop || !isSidebarExpanded) return 2;
      return 2 + level * 2;
    }, [isDesktop, isSidebarExpanded, level]);

    return (
      <>
        <ListItemButton
          disabled={item.disabled}
          onClick={handleClick}
          sx={{
            pl: paddingLeft,
            justifyContent:
              isDesktop && !isSidebarExpanded ? "center" : "flex-start",
          }}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: isDesktop && !isSidebarExpanded ? 0 : 40,
                justifyContent: "center",
              }}
            >
              {item.icon as ReactNode}
            </ListItemIcon>
          )}
          {(isSidebarExpanded || !isDesktop) && (
            <>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  noWrap: true,
                  fontWeight: isActive ? 600 : 400,
                }}
              />
              {hasChildren &&
                (isGroupOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </>
          )}
        </ListItemButton>

        {hasChildren && (isSidebarExpanded || !isDesktop) && (
          <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children!.map((child) => (
                <SidebarItem
                  key={child.id}
                  item={child}
                  level={level + 1}
                  isDesktop={isDesktop}
                  isSidebarExpanded={isSidebarExpanded}
                  activePath={activePath}
                  onNavigate={onNavigate}
                  isGroupOpen={isGroupOpen}
                  toggleGroup={toggleGroup}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  },
);

SidebarItem.displayName = "SidebarItem";

/**
 * Sidebar genérico compartilhado entre aplicações.
 *
 * - Responsável apenas por layout e comportamento visual do menu.
 * - Não conhece contexto de autenticação ou detalhes de domínio.
 * - Utiliza SharedMenuItem como contrato mínimo para navegação.
 */
const SharedSidebar = memo<SharedSidebarProps>(
  ({ items, expanded, setExpanded, container }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const navigate = useNavigate();
    const location = useLocation();

    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] =
      useState<PrototypeSignatureProvider>("VIDAAS");

    const {
      session,
      isSessionActive,
      isStarting,
      isTerminating,
      errorMessage,
      startSession,
      endSession,
    } = usePrototypeSignatureSession();

    const drawerWidth = isDesktop
      ? expanded
        ? DRAWER_WIDTH_EXPANDED
        : DRAWER_WIDTH_COLLAPSED
      : DRAWER_WIDTH_EXPANDED;

    const handleToggleDesktop = useCallback(() => {
      if (!isDesktop) return;
      setExpanded(!expanded);
    }, [isDesktop, expanded, setExpanded]);

    const handleNavigate = useCallback(
      (item: SharedMenuItem) => {
        if (!item.path) return;

        navigate(item.path, { state: item.navigationState });
        if (!isDesktop) {
          setExpanded(false);
        }
      },
      [navigate, isDesktop, setExpanded],
    );

    const handleToggleGroup = useCallback((id: string) => {
      setOpenGroups((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    }, []);

    const isSidebarCollapsed = isDesktop && !expanded;

    const handleOpenSignatureDialog = useCallback(() => {
      if (session) {
        setSelectedProvider(session.provider);
      }
      setIsSignatureDialogOpen(true);
    }, [session]);

    const handleCloseSignatureDialog = useCallback(() => {
      if (isStarting || isTerminating) return;
      setIsSignatureDialogOpen(false);
    }, [isStarting, isTerminating]);

    const handleClickSignatureSection = useCallback(() => {
      handleOpenSignatureDialog();
    }, [handleOpenSignatureDialog]);

    const handleChangeProvider = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as PrototypeSignatureProvider;
        setSelectedProvider(value);
      },
      [],
    );

    const handleConfirmStartSession = useCallback(async () => {
      if (isStarting) return;
      await startSession(selectedProvider);
      setIsSignatureDialogOpen(false);
    }, [isStarting, selectedProvider, startSession]);

    const handleConfirmEndSession = useCallback(async () => {
      if (isTerminating) return;
      await endSession();
      setIsSignatureDialogOpen(false);
    }, [endSession, isTerminating]);

    const drawerContent = (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/*
         * Spacer puro: ocupa exatamente a altura do AppBar fixo.
         * NÃO colocar conteúdo aqui — o AppBar tem zIndex: drawer+1
         * e renderiza por cima desta área, ocultando qualquer elemento.
         */}
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, flexShrink: 0 }} />

        <Divider />

        {/*
         * Linha do cabeçalho da sidebar — fica ABAIXO do AppBar, portanto visível.
         * Desktop: mostra título (quando expandido) + botão de collapse.
         * Mobile: mostra apenas o título.
         */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isDesktop
              ? expanded
                ? "space-between"
                : "center"
              : "flex-start",
            px: expanded || !isDesktop ? 2 : 1,
            py: 0.75,
            minHeight: 40,
            flexShrink: 0,
          }}
        >
          {(expanded || !isDesktop) && (
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              Menu
            </Typography>
          )}

          {isDesktop && (
            <IconButton
              onClick={handleToggleDesktop}
              size="small"
              aria-label={expanded ? "colapsar sidebar" : "expandir sidebar"}
              sx={{ ml: expanded ? 0.5 : 0 }}
            >
              {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </Box>

        <Divider />

        <List sx={{ flexGrow: 1 }}>
          {items.map((item) => {
            const isGroupOpen = openGroups[item.id] ?? true;

            return (
              <SidebarItem
                key={item.id}
                item={item}
                level={0}
                isDesktop={isDesktop}
                isSidebarExpanded={expanded}
                activePath={location.pathname}
                onNavigate={handleNavigate}
                isGroupOpen={isGroupOpen}
                toggleGroup={() => handleToggleGroup(item.id)}
              />
            );
          })}
        </List>

        {/* Seção de controle da sessão de assinatura digital (protótipo) */}
        <Box
          sx={{
            mt: "auto",
            pb: 2,
            px: isSidebarCollapsed ? 1 : 2,
          }}
        >
          <Divider sx={{ mb: 1.5 }} />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              px: isSidebarCollapsed ? 0.5 : 1,
              py: 1,
              borderRadius: 1,
              cursor: "pointer",
              justifyContent: isSidebarCollapsed ? "center" : "flex-start",
              "&:hover": {
                backgroundColor: (themeValue) =>
                  themeValue.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.04)"
                    : "rgba(0, 0, 0, 0.03)",
              },
              transition: "background-color 0.2s ease",
            }}
            onClick={handleClickSignatureSection}
          >
            <FingerprintIcon
              fontSize="small"
              color="primary"
              sx={{ flexShrink: 0 }}
            />

            {(!isSidebarCollapsed || !isDesktop) && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: 0.2,
                    textTransform: "uppercase",
                    display: "block",
                    mb: 0.25,
                  }}
                >
                  Assinatura digital
                </Typography>

                {isSessionActive && session ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.2,
                      }}
                    >
                      Provedor:{" "}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {session.providerLabel}
                      </Box>
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      Sessão expira em{" "}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {session.remainingLabel}
                      </Box>
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    Nenhuma sessão ativa. Clique para iniciar.
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    );

    return (
      <>
        {isSignatureDialogOpen && (
          <Dialog
            open={isSignatureDialogOpen}
            onClose={handleCloseSignatureDialog}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>
              {isSessionActive
                ? "Sessão de assinatura ativa"
                : "Iniciar sessão de assinatura"}
            </DialogTitle>

            <DialogContent dividers>
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              {isSessionActive && session ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Você está utilizando o provedor{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {session.providerLabel}
                    </Box>
                    .
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sessão expira em{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {session.remainingLabel}
                    </Box>
                    .
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selecione o provedor de assinatura digital para iniciar uma
                    nova sessão.
                  </Typography>
                  <RadioGroup
                    name="signature-provider"
                    value={selectedProvider}
                    onChange={handleChangeProvider}
                  >
                    <FormControlLabel
                      value="VIDAAS"
                      control={<Radio />}
                      label="Vidaas"
                    />
                    <FormControlLabel
                      value="SAFEID"
                      control={<Radio />}
                      label="SafeId"
                    />
                  </RadioGroup>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleCloseSignatureDialog}
                disabled={isStarting || isTerminating}
              >
                Fechar
              </Button>

              {isSessionActive ? (
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleConfirmEndSession}
                  disabled={isTerminating}
                  endIcon={
                    isTerminating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                >
                  {isTerminating ? "Terminando..." : "Terminar sessão"}
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleConfirmStartSession}
                  disabled={isStarting}
                  endIcon={
                    isStarting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                >
                  {isStarting ? "Iniciando..." : "Iniciar sessão"}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )}

        <Box
          component="nav"
          sx={{
            width: isDesktop ? drawerWidth : 0,
            flexShrink: 0,
          }}
          aria-label="menu de navegação"
        >
          {/* Drawer temporário para mobile */}
          <Drawer
            container={container}
            variant="temporary"
            open={!isDesktop && expanded}
            onClose={() => setExpanded(false)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: DRAWER_WIDTH_EXPANDED,
              },
            }}
          >
            {drawerContent}
          </Drawer>

          {/* Drawer permanente para desktop */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                overflowX: "hidden",
              },
            }}
            open={isDesktop}
          >
            {drawerContent}
          </Drawer>
        </Box>
      </>
    );
  },
);

SharedSidebar.displayName = "SharedSidebar";

export default SharedSidebar;
